import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, X, ArrowRight, CornerDownLeft, ArrowUp, ArrowDown,
  LayoutDashboard, CalendarDays, ListChecks, FileText, DollarSign,
  Kanban, UserCircle, AlertTriangle, Wrench, BoxSelect, Package,
  ClipboardList, BarChart3, Users, Tag, Bot, HardDrive, Settings,
  BookOpen, FolderOpen, ScrollText, Zap, Star, Clock,
} from "lucide-react";
import axios from "axios";
import { API_V1 } from "../config/api";

const ICON_MAP = {
  dashboard: LayoutDashboard, calendar: CalendarDays, tasks: ListChecks,
  submissions: FileText, customers: UserCircle, quotes: DollarSign,
  projects: Kanban, documents: FileText, templates: BookOpen,
  files: HardDrive, protocols: ScrollText, contracts: FileText,
  subsidies: Zap, claims: AlertTriangle, service: Wrench,
  rentals: BoxSelect, items_catalog: Package, revize: ClipboardList,
  penb: BarChart3, professionals: Users, categories: Tag,
  users: Users, ai_agent: Bot, data_management: HardDrive,
  audit_log: ClipboardList, settings: Settings,
};

const MODULE_ICON_BY_COLLECTION = {
  customers: UserCircle, submissions: FileText, quotes: DollarSign,
  projects: Kanban, tasks: ListChecks, calendar_events: CalendarDays,
  claims: AlertTriangle, service_orders: Wrench, contracts: FileText,
  documents: FileText, protocols: ScrollText, subsidies: Zap,
  professionals: Users, rentals: BoxSelect, revize_objects: ClipboardList,
};

// Navigation items for quick access
const NAV_ITEMS = [
  { path: "/admin/dashboard", label: "Dashboard", moduleKey: "dashboard", keywords: ["hlavní", "přehled"] },
  { path: "/admin/calendar", label: "Kalendář", moduleKey: "calendar", keywords: ["události", "schůzky"] },
  { path: "/admin/tasks", label: "Úkoly", moduleKey: "tasks", keywords: ["todo", "práce"] },
  { path: "/admin/submissions", label: "Poptávky", moduleKey: "submissions", keywords: ["formulář", "leads"] },
  { path: "/admin/customers", label: "Zákazníci", moduleKey: "customers", keywords: ["klienti", "kontakty"] },
  { path: "/admin/quotes", label: "Nabídky", moduleKey: "quotes", keywords: ["cenová", "kalkulace"] },
  { path: "/admin/projects", label: "Projekty", moduleKey: "projects", keywords: ["zakázky"] },
  { path: "/admin/documents", label: "Dokumenty", moduleKey: "documents", keywords: ["soubory"] },
  { path: "/admin/templates", label: "Šablony", moduleKey: "templates", keywords: ["vzory"] },
  { path: "/admin/files", label: "Soubory", moduleKey: "files", keywords: ["přílohy"] },
  { path: "/admin/protocols", label: "Protokoly", moduleKey: "protocols", keywords: ["zápisy"] },
  { path: "/admin/contracts", label: "Smlouvy", moduleKey: "contracts", keywords: ["dohody"] },
  { path: "/admin/subsidies", label: "Dotace", moduleKey: "subsidies", keywords: ["podpora"] },
  { path: "/admin/claims", label: "Reklamace", moduleKey: "claims", keywords: ["stížnosti"] },
  { path: "/admin/service", label: "Servis", moduleKey: "service", keywords: ["opravy"] },
  { path: "/admin/rentals", label: "Půjčovna", moduleKey: "rentals", keywords: ["pronájem"] },
  { path: "/admin/items-catalog", label: "Katalog položek", moduleKey: "items_catalog", keywords: ["produkty"] },
  { path: "/admin/revize", label: "Revize", moduleKey: "revize", keywords: ["kontroly", "inspekce"] },
  { path: "/admin/penb", label: "PENB", moduleKey: "penb", keywords: ["energetický", "průkaz"] },
  { path: "/admin/professionals", label: "Řemeslníci", moduleKey: "professionals", keywords: ["dodavatelé"] },
  { path: "/admin/settings", label: "Nastavení", moduleKey: "settings", keywords: ["konfigurace"] },
];

function getRecentSearches() {
  try {
    return JSON.parse(localStorage.getItem("cmd_recent") || "[]").slice(0, 5);
  } catch { return []; }
}
function addRecentSearch(item) {
  try {
    const recent = getRecentSearches().filter(r => r.path !== item.path);
    recent.unshift({ path: item.path, label: item.label, moduleKey: item.moduleKey });
    localStorage.setItem("cmd_recent", JSON.stringify(recent.slice(0, 5)));
  } catch {}
}

export default function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [apiResults, setApiResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIdx(0);
      setApiResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Debounced API search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) {
      setApiResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_V1}/search`, {
          params: { q: query, limit: 4 },
          withCredentials: true,
        });
        setApiResults(res.data.results || []);
      } catch {
        setApiResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Build flat result list
  const navResults = query.length > 0
    ? NAV_ITEMS.filter(item => {
        const q = query.toLowerCase();
        return item.label.toLowerCase().includes(q) ||
          item.moduleKey.toLowerCase().includes(q) ||
          item.keywords.some(k => k.includes(q));
      })
    : [];

  const recentItems = query.length === 0 ? getRecentSearches() : [];

  // Build sections
  const sections = [];
  if (recentItems.length > 0) {
    sections.push({ title: "Nedávné", items: recentItems.map(r => ({ ...r, type: "nav" })) });
  }
  if (navResults.length > 0) {
    sections.push({ title: "Navigace", items: navResults.map(n => ({ ...n, type: "nav" })) });
  }
  if (apiResults.length > 0) {
    // Group by module
    const grouped = {};
    apiResults.forEach(r => {
      if (!grouped[r.module]) grouped[r.module] = [];
      grouped[r.module].push({ ...r, type: "record" });
    });
    Object.entries(grouped).forEach(([module, items]) => {
      sections.push({ title: module, items });
    });
  }

  const flatItems = sections.flatMap(s => s.items);
  const totalItems = flatItems.length;

  // Clamp activeIdx
  useEffect(() => {
    if (activeIdx >= totalItems) setActiveIdx(Math.max(0, totalItems - 1));
  }, [totalItems, activeIdx]);

  const navigate = useNavigate();

  const handleSelect = useCallback((item) => {
    if (item.type === "nav") {
      addRecentSearch(item);
      navigate(item.path);
    } else if (item.type === "record") {
      navigate(item.route);
    }
    onClose();
  }, [navigate, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, totalItems - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && flatItems[activeIdx]) {
      e.preventDefault();
      handleSelect(flatItems[activeIdx]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  if (!isOpen) return null;

  let globalIdx = -1;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" data-testid="command-palette">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        data-testid="command-palette-modal">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Hledejte moduly, zákazníky, projekty..."
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400"
            data-testid="command-palette-input"
          />
          {loading && (
            <div className="w-4 h-4 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin" />
          )}
          <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-100 text-[10px] text-slate-500 font-mono">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[360px] overflow-y-auto" data-testid="command-palette-results">
          {sections.length === 0 && query.length > 0 && !loading && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              Žádné výsledky pro "{query}"
            </div>
          )}
          {sections.length === 0 && query.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              Začněte psát pro vyhledávání...
            </div>
          )}

          {sections.map((section, sIdx) => (
            <div key={sIdx}>
              <div className="px-4 pt-3 pb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {section.title}
                </span>
              </div>
              {section.items.map((item, iIdx) => {
                globalIdx++;
                const idx = globalIdx;
                const isActive = idx === activeIdx;
                const Icon = item.type === "nav"
                  ? (ICON_MAP[item.moduleKey] || FileText)
                  : (MODULE_ICON_BY_COLLECTION[item.collection] || FileText);

                return (
                  <button
                    key={`${sIdx}-${iIdx}`}
                    data-idx={idx}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`command-result-${idx}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-emerald-600" : "text-slate-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {item.type === "nav" ? item.label : item.title}
                      </div>
                      {item.type === "record" && item.subtitle && (
                        <div className="text-xs text-slate-400 truncate">{item.subtitle}</div>
                      )}
                    </div>
                    {item.type === "nav" && (
                      <ArrowRight className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? "text-emerald-500" : "text-slate-300"}`} />
                    )}
                    {item.type === "record" && (
                      <span className="text-[10px] text-slate-400 flex-shrink-0">Otevřít</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer hints */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-slate-100 bg-slate-50/50">
          <span className="flex items-center gap-1 text-[10px] text-slate-400">
            <ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" /> navigace
          </span>
          <span className="flex items-center gap-1 text-[10px] text-slate-400">
            <CornerDownLeft className="w-3 h-3" /> vybrat
          </span>
          <span className="flex items-center gap-1 text-[10px] text-slate-400">
            esc zavřít
          </span>
        </div>
      </div>
    </div>
  );
}
